import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { syncPMEToFinancial } from '../utils/moduleSync';

export interface PMEFixedExpense {
  id: string;
  user_id: string;
  label: string;
  amount: number;
  frequency: 'monthly' | 'quarterly' | 'yearly';
  next_payment_date: string;
  is_active: boolean;
  notes: string;
  created_at: string;
  updated_at: string;
}

export type PMEFixedExpenseInsert = Omit<PMEFixedExpense, 'id' | 'user_id' | 'created_at' | 'updated_at'>;

export function usePMEFixedExpenses() {
  const { effectiveUserId } = useAuth();
  const [expenses, setExpenses] = useState<PMEFixedExpense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!effectiveUserId) return;
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('pme_fixed_expenses')
      .select('*')
      .eq('user_id', effectiveUserId)
      .order('next_payment_date', { ascending: true });

    if (err) {
      setError(err.message);
    } else {
      setExpenses(data || []);
    }
    setLoading(false);
  }, [effectiveUserId]);

  useEffect(() => {
    load();
  }, [load]);

  async function addExpense(expense: PMEFixedExpenseInsert) {
    if (!effectiveUserId) return;
    const { error: err } = await supabase
      .from('pme_fixed_expenses')
      .insert({ ...expense, user_id: effectiveUserId });
    if (err) throw new Error(err.message);
    await load();
    syncPMEToFinancial(effectiveUserId);
  }

  async function updateExpense(id: string, updates: Partial<PMEFixedExpenseInsert>) {
    const { error: err } = await supabase
      .from('pme_fixed_expenses')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (err) throw new Error(err.message);
    await load();
    syncPMEToFinancial(effectiveUserId!);
  }

  async function deleteExpense(id: string) {
    const { error: err } = await supabase
      .from('pme_fixed_expenses')
      .delete()
      .eq('id', id);
    if (err) throw new Error(err.message);
    await load();
    syncPMEToFinancial(effectiveUserId!);
  }

  const activeExpenses = expenses.filter(e => e.is_active);

  function getMonthlyEquivalent(expense: PMEFixedExpense): number {
    switch (expense.frequency) {
      case 'monthly': return expense.amount;
      case 'quarterly': return expense.amount / 3;
      case 'yearly': return expense.amount / 12;
    }
  }

  const totalMonthlyBurden = activeExpenses.reduce(
    (sum, e) => sum + getMonthlyEquivalent(e),
    0
  );

  function getProjectedExpensesForPeriod(days: number): number {
    const today = new Date();
    const end = new Date(today);
    end.setDate(end.getDate() + days);

    let total = 0;
    for (const expense of activeExpenses) {
      const next = new Date(expense.next_payment_date);
      if (next <= end) {
        total += expense.amount;
        if (expense.frequency === 'monthly' && days >= 30) {
          total += expense.amount * Math.floor((days - 30) / 30);
        }
      }
    }
    return total;
  }

  return {
    expenses,
    activeExpenses,
    totalMonthlyBurden,
    loading,
    error,
    addExpense,
    updateExpense,
    deleteExpense,
    getMonthlyEquivalent,
    getProjectedExpensesForPeriod,
    refresh: load,
  };
}
